<template>
  <div class="page-view">

    <BasePageHeader :title="$t('serviceHistory.title')" @back="$router.back()">
      <template #actions>
        <router-link v-if="isOperatorOrAbove" to="/maintenance/history/new" class="btn primary small">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="w-4 h-4">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          {{ $t('common.new') }}
        </router-link>
      </template>
    </BasePageHeader>

    <!-- Tab strip -->
    <div class="flex border-b border-[var(--color-border)]">
      <router-link
        to="/maintenance/schedules"
        class="flex-1 text-center py-3 px-2 text-[.875rem] font-semibold text-[var(--color-muted)] no-underline border-b-2 border-b-transparent transition-colors hover:text-[var(--color-text)]"
      >{{ $t('serviceHistory.schedulesTab') }}</router-link>
      <router-link
        to="/maintenance/history"
        class="flex-1 text-center py-3 px-2 text-[.875rem] font-semibold no-underline border-b-2 transition-colors text-[var(--color-accent)] border-b-[var(--color-primary)]"
      >{{ $t('serviceHistory.historyTab') }}</router-link>
    </div>

    <!-- Filters -->
    <div class="flex gap-2 items-center flex-wrap px-4 py-3 border-b border-[var(--color-border)]">
      <BaseFormField density="compact" class="flex-1 min-w-[100px] !mb-0">
        <BaseSelect v-model="filters.maintenance_type" density="compact" :aria-label="$t('serviceHistory.allTypes')">
          <option value="">{{ $t('serviceHistory.allTypes') }}</option>
          <option v-for="t in MAINT_TYPES" :key="t.value" :value="t.value">{{ t.label }}</option>
        </BaseSelect>
      </BaseFormField>
      <BaseFormField density="compact" class="flex-1 min-w-[100px] !mb-0">
        <BaseSelect v-model="filters.status" density="compact" :aria-label="$t('serviceHistory.allStatuses')">
          <option value="">{{ $t('serviceHistory.allStatuses') }}</option>
          <option value="pending">{{ $t('serviceHistory.statuses.pending') }}</option>
          <option value="in_progress">{{ $t('serviceHistory.statuses.in_progress') }}</option>
          <option value="completed">{{ $t('serviceHistory.statuses.completed') }}</option>
          <option value="failed">{{ $t('serviceHistory.statuses.failed') }}</option>
        </BaseSelect>
      </BaseFormField>
      <BaseFormField density="compact" class="flex-1 min-w-[100px] !mb-0">
        <BaseInput v-model="filters.start_date" type="date" density="compact" :aria-label="$t('serviceHistory.fromDate')" :title="$t('serviceHistory.fromDate')" />
      </BaseFormField>
      <BaseFormField density="compact" class="flex-1 min-w-[100px] !mb-0">
        <BaseInput v-model="filters.end_date" type="date" density="compact" :aria-label="$t('serviceHistory.toDate')" :title="$t('serviceHistory.toDate')" />
      </BaseFormField>
      <button class="btn primary small shrink-0" @click="load">{{ $t('common.apply') }}</button>
    </div>

    <!-- Loading / error / empty -->
    <EmptyState v-if="loading" :message="$t('common.loading')" />
    <EmptyState v-else-if="loadError" :message="loadError" variant="error" />
    <EmptyState v-else-if="!history.length" :message="$t('serviceHistory.noHistory')" />

    <!-- List -->
    <div v-else class="list-stack">
      <div
        v-for="h in history" :key="h.id"
        class="data-card flex-col gap-2 !items-start"
      >
        <div class="flex gap-4">
          <div class="flex flex-col gap-1 flex-1 min-w-0">
            <div class="flex items-start justify-between gap-2">
              <div class="data-card__name">{{ h.title }}</div>
              <span class="badge shrink-0" :class="statusBadgeClass(h.status)">{{ STATUS_LABELS[h.status] ?? h.status }}</span>
            </div>
            <div class="flex flex-wrap gap-1.5 items-center mt-0.5">
              <span v-if="h.maintenance_type" class="badge" :class="typeBadgeClass(h.maintenance_type)">{{ MAINT_LABELS[h.maintenance_type] ?? h.maintenance_type }}</span>
              <span v-if="h.service_date" class="badge badge-muted">{{ formatDate(h.service_date) }}</span>
              <span v-if="h.performed_by" class="badge badge-muted">{{ userName(h.performed_by) }}</span>
              <span v-if="h.actual_hours != null && h.actual_hours !== ''" class="badge badge-muted font-medium">{{ h.actual_hours }}h</span>
            </div>
            <div v-if="h.item_metadata?.length" class="flex flex-wrap gap-1 mt-1">
              <span v-for="inst in h.item_metadata" :key="inst.id ?? inst" class="badge badge-muted badge-pill">{{ instanceLabel(inst) }}</span>
            </div>
            <div v-if="h.findings" class="text-[.78rem] text-[var(--color-muted)] mt-1 leading-snug">
              <span class="font-semibold text-[var(--color-text)]">{{ $t('serviceHistory.findingsLabel') }}</span> {{ h.findings }}
            </div>
            <div v-if="h.actions_taken" class="text-[.78rem] text-[var(--color-muted)] mt-1 leading-snug">
              <span class="font-semibold text-[var(--color-text)]">{{ $t('serviceHistory.actionsLabel') }}</span> {{ h.actions_taken }}
            </div>
            <div v-if="h.description" class="text-[.78rem] text-[var(--color-muted)] mt-1 leading-snug">{{ h.description }}</div>
            <!-- Image below text on mobile -->
            <div v-if="h.image_url" class="mt-2 sm:hidden">
              <img
                :src="h.image_url"
                :alt="$t('serviceHistory.photoAlt')"
                class="max-h-40 w-full rounded-lg border border-[var(--color-border)] object-contain bg-black/20 cursor-zoom-in"
                @click="lightboxSrc = h.image_url"
              />
            </div>
          </div>
          <!-- Image on right on desktop -->
          <div v-if="h.image_url" class="hidden sm:flex shrink-0 items-start">
            <img
              :src="h.image_url"
              :alt="$t('serviceHistory.photoAlt')"
              class="h-28 w-40 rounded-lg border border-[var(--color-border)] object-cover bg-black/20 cursor-zoom-in"
              @click="lightboxSrc = h.image_url"
            />
          </div>
        </div>

        <!-- Row actions -->
        <div class="flex gap-2 flex-wrap pt-2 border-t border-[var(--color-border)]">
          <button
            v-if="h.status !== 'completed' && isOperatorOrAbove"
            class="btn primary small"
            :disabled="completingId === h.id || failingId === h.id"
            @click="markComplete(h)"
          >{{ completingId === h.id ? $t('common.saving') : $t('serviceHistory.markComplete') }}</button>
          <button
            v-if="h.status !== 'failed' && h.status !== 'completed' && isOperatorOrAbove"
            class="btn danger small"
            :disabled="completingId === h.id || failingId === h.id"
            @click="markFailed(h)"
          >{{ failingId === h.id ? $t('common.saving') : $t('serviceHistory.markFailed') }}</button>
          <router-link v-if="isOperatorOrAbove" :to="`/maintenance/history/${h.id}/edit`" class="btn ghost small">{{ $t('serviceHistory.edit') }}</router-link>
          <button v-if="isOperatorOrAbove" class="btn danger small" @click="openDelete(h)">{{ $t('common.delete') }}</button>
        </div>
      </div>
    </div>

  </div>

  <!-- Lightbox -->
  <Teleport to="body">
    <div
      v-if="lightboxSrc"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/85"
      @click="lightboxSrc = null"
    >
      <button
        class="absolute top-4 right-4 btn-icon z-10"
        @click.stop="lightboxSrc = null"
        :aria-label="$t('common.close')"
      >
        <MdiClose class="w-5 h-5" />
      </button>
      <img
        :src="lightboxSrc"
        class="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
        @click.stop
      />
    </div>
  </Teleport>

  <!-- Delete confirm -->
  <BaseConfirmModal
    :show="!!deleteTarget"
    :title="$t('serviceHistory.deleteConfirmTitle')"
    :body="deleteTarget ? $t('serviceHistory.deleteConfirmBody', { title: deleteTarget.title }) : ''"
    :confirm-label="$t('common.delete')"
    confirm-variant="delete"
    :loading="deleting"
    @confirm="confirmDelete"
    @cancel="deleteTarget = null"
  />

</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from '@/composables/useToast'
import { usePermissions } from '@/composables/usePermissions.js'
import { getServiceHistory, updateServiceHistory, deleteServiceHistory } from '@/api/api.js'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import BaseConfirmModal from '@/components/base/BaseConfirmModal.vue'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import EmptyState from '@/components/base/EmptyState.vue'
import MdiClose from '~icons/mdi/close'

const route = useRoute()
const toast = useToast()
const { t } = useI18n()
const { isOperatorOrAbove } = usePermissions()

const lightboxSrc = ref(null)

const MAINT_TYPES = computed(() => [
  { value: 'preventive',  label: t('serviceHistory.types.preventive') },
  { value: 'corrective',  label: t('serviceHistory.types.corrective') },
  { value: 'inspection',  label: t('serviceHistory.types.inspection') },
  { value: 'calibration', label: t('serviceHistory.types.calibration') },
])
const MAINT_LABELS = computed(() => Object.fromEntries(MAINT_TYPES.value.map(t => [t.value, t.label])))

const STATUS_LABELS = computed(() => ({
  pending:     t('serviceHistory.statuses.pending'),
  in_progress: t('serviceHistory.statuses.in_progress'),
  completed:   t('serviceHistory.statuses.completed'),
  failed:      t('serviceHistory.statuses.failed'),
}))

const history   = ref([])
const loading   = ref(false)
const loadError = ref(null)

const filters = ref({
  maintenance_type: '',
  status:           '',
  start_date:       '',
  end_date:         '',
  item_metadata:    route.query.instance ?? '',
})

watch(() => route.query.instance, (val) => {
  filters.value.item_metadata = val ?? ''
  load()
}, { immediate: false })

async function load() {
  loading.value   = true
  loadError.value = null
  try {
    const params = {}
    if (filters.value.maintenance_type) params.maintenance_type = filters.value.maintenance_type
    if (filters.value.status)           params.status           = filters.value.status
    if (filters.value.start_date)       params.start_date       = filters.value.start_date
    if (filters.value.end_date)         params.end_date         = filters.value.end_date
    if (filters.value.item_metadata)    params.item_metadata    = filters.value.item_metadata
    const res = await getServiceHistory(params)
    history.value = res.results ?? res ?? []
  } catch (e) {
    loadError.value = e?.data?.detail ?? e?.message ?? t('serviceHistory.loadFailed')
  } finally {
    loading.value = false
  }
}

onMounted(load)

// ── quick status actions ──────────────────────────────────────────────
const completingId = ref(null)
const failingId    = ref(null)

async function markComplete(h) {
  completingId.value = h.id
  try {
    await updateServiceHistory(h.id, { status: 'completed' })
    h.status = 'completed'
    toast.success(t('serviceHistory.markedCompleted'))
  } catch (e) {
    toast.error(e?.data?.detail ?? e?.message ?? t('serviceHistory.updateStatusFailed'))
  } finally {
    completingId.value = null
  }
}

async function markFailed(h) {
  failingId.value = h.id
  try {
    await updateServiceHistory(h.id, { status: 'failed' })
    h.status = 'failed'
    toast.success(t('serviceHistory.markedFailed'))
  } catch (e) {
    toast.error(e?.data?.detail ?? e?.message ?? t('serviceHistory.updateStatusFailed'))
  } finally {
    failingId.value = null
  }
}

// ── delete ────────────────────────────────────────────────────────────
const deleteTarget = ref(null)
const deleting     = ref(false)

function openDelete(h) { deleteTarget.value = h }

async function confirmDelete() {
  deleting.value = true
  try {
    await deleteServiceHistory(deleteTarget.value.id)
    toast.success(t('serviceHistory.deleted'))
    deleteTarget.value = null
    await load()
  } catch (e) {
    toast.error(e?.data?.detail ?? e?.message ?? t('serviceHistory.deleteFailed'))
  } finally {
    deleting.value = false
  }
}

// ── helpers ───────────────────────────────────────────────────────────
function formatDate(d) {
  if (!d) return '—'
  try { return new Date(d + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) }
  catch { return d }
}

function userName(u) {
  if (!u) return ''
  if (typeof u === 'object') return [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || `#${u.id}`
  return String(u)
}

function instanceLabel(inst) {
  if (!inst) return ''
  if (typeof inst === 'object') return inst.asset_tag ?? inst.serial_number ?? inst.item?.name ?? `#${inst.id}`
  return `#${inst}`
}

function statusBadgeClass(s) {
  return { pending: 'badge-warning', in_progress: 'badge-accent', completed: 'badge-success', failed: 'badge-danger' }[s] ?? 'badge-muted'
}

function typeBadgeClass(t) {
  return { preventive: 'badge-accent', corrective: 'badge-warning', inspection: 'badge-muted', calibration: 'badge-accent' }[t] ?? 'badge-muted'
}
</script>
